import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface NewsletterProps {
  className?: string;
}

const Newsletter: React.FC<NewsletterProps> = ({ className = '' }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Input handler for better input performance
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  
  // Form submission handler with custom handling
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || email.indexOf('@') === -1) {
      setError('Please enter a valid email address.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Create a FormData object from the form
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      
      // Submit the form data to Mailchimp's endpoint using fetch
      await fetch(
        'https://sheeshka.us15.list-manage.com/subscribe/post?u=bba83329995483758dbd7f9f2&id=1bb6d91a87&f_id=00d495e0f0',
        {
          method: 'POST',
          mode: 'no-cors', // Important for cross-origin requests to Mailchimp
          body: formData
        }
      );
      
      // Since no-cors mode returns an opaque response, we just assume success
      // Redirect to success page
      navigate('/subscription-success');
    } catch (err) {
      setError('Something went wrong. Please try again later.');
      console.error('Newsletter subscription error:', err);
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className={`${className}`}>
      <div className="flex flex-col md:flex-row items-center justify-between">
        <div className="mb-6 md:mb-0 md:mr-8">
          <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
          <p className="text-gray-300">Get notified when I publish new articles</p>
        </div>
        <div className="w-full md:w-auto">
          <form 
            ref={formRef}
            onSubmit={handleSubmit}
            id="mc-embedded-subscribe-form" 
            name="mc-embedded-subscribe-form" 
            className="flex w-full flex-col md:flex-row" 
          >
            <div className="flex w-full">
              <input 
                type="email" 
                name="EMAIL" 
                id="mce-EMAIL"
                value={email}
                onChange={handleEmailChange}
                placeholder="Your email address" 
                className="w-full md:w-64 px-4 py-2 rounded-l-lg bg-[#0F172A] border border-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button 
                type="submit"
                name="subscribe"
                id="mc-embedded-subscribe"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-r-lg transition-colors disabled:opacity-70"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Subscribing...
                  </span>
                ) : 'Subscribe'}
              </button>
            </div>
            
            {/* Mailchimp required fields */}
            <input type="hidden" name="WEBSITE" value="davidsheeshka.com" />
            
            {/* This input is for Mailchimp */}
            <div className="absolute -left-[5000px]" aria-hidden="true">
              <input type="text" name="b_bba83329995483758dbd7f9f2_1bb6d91a87" tabIndex={-1} value="" />
            </div>
            
            {error && (
              <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-800 rounded text-sm">
                {error}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Newsletter;
